import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Client } from '../types.js';
import { listClients, archiveClient } from '../api.js';

export default function ClientsList() {
  const [clients, setClients] = useState<Client[]>([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  async function load() {
    setLoading(true);
    setError('');
    try {
      const res = await listClients(page, search);
      setClients(res.clients);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, [page, search]);

  async function handleArchive(client: Client) {
    if (!confirm(`Archive "${client.name}"? Their submissions will be retained for 30 days.`)) return;
    try {
      await archiveClient(client.id);
      await load();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Archive failed');
    }
  }

  const statusBadge = (s: Client['status']) => {
    if (s === 'active') return <span className="badge badge-green">Active</span>;
    if (s === 'paused') return <span className="badge badge-yellow">Paused</span>;
    return <span className="badge badge-grey">Archived</span>;
  };

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Clients</h1>
        <Link to="/clients/new" className="btn btn-primary">+ New client</Link>
      </div>

      <div className="card">
        <div style={{ display: 'flex', gap: '.75rem', marginBottom: '1rem' }}>
          <input
            type="text"
            placeholder="Search by name…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{ maxWidth: '260px' }}
          />
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {loading ? (
          <p style={{ color: '#94a3b8' }}>Loading…</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>ID</th>
                <th>Status</th>
                <th>Email</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {clients.length === 0 && (
                <tr><td colSpan={6} style={{ color: '#94a3b8', padding: '1rem 0' }}>No clients found.</td></tr>
              )}
              {clients.map((c) => (
                <tr key={c.id}>
                  <td><Link to={`/clients/${c.id}`}>{c.name}</Link></td>
                  <td><code style={{ fontSize: '.8rem' }}>{c.id}</code></td>
                  <td>
                    {statusBadge(c.status)}
                    {c.suspended_at !== undefined && (
                      <span className="badge badge-red" style={{ marginLeft: '.35rem' }}>Suspended</span>
                    )}
                  </td>
                  <td>{c.email_enabled ? '✓' : '—'}</td>
                  <td>{new Date(c.created_at * 1000).toLocaleDateString('en-AU')}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <Link to={`/clients/${c.id}/edit`} className="btn btn-secondary btn-sm" style={{ marginRight: '.4rem' }}>Edit</Link>
                    <Link to={`/clients/${c.id}/submissions`} className="btn btn-secondary btn-sm" style={{ marginRight: '.4rem' }}>Submissions</Link>
                    <button className="btn btn-danger btn-sm" onClick={() => handleArchive(c)}>Archive</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="pagination">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>← Prev</button>
          <span>Page {page}</span>
          <button onClick={() => setPage((p) => p + 1)} disabled={clients.length < 20}>Next →</button>
        </div>
      </div>
    </>
  );
}
